import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useWishlist } from '../contexts/WishlistContext';
import { ArrowLeft, ShoppingCart, Tag, Package, Star, ThumbsUp, ShieldCheck, X, Heart, Plus, Clock } from 'lucide-react';
import PromoSlot, { useCountdown } from '../components/PromoBanner';
import { needsChoices, displayPrice } from '../lib/cartLine';
import { addRecentlyViewed } from '../lib/recentlyViewed';
import PriceTag from '../components/PriceTag';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Star display helper ──────────────────────────────────────
function Stars({ rating, size = 'sm', interactive = false, onRate }) {
  const [hovered, setHovered] = useState(0);
  const sz = size === 'lg' ? 'w-7 h-7' : size === 'md' ? 'w-5 h-5' : 'w-4 h-4';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i}
          className={`${sz} transition-colors ${interactive ? 'cursor-pointer' : ''} ${
            i <= (interactive ? (hovered || rating) : rating)
              ? 'fill-honey text-honey-600'
              : 'text-ink/30'
          }`}
          onMouseEnter={() => interactive && setHovered(i)}
          onMouseLeave={() => interactive && setHovered(0)}
          onClick={() => interactive && onRate && onRate(i)}
        />
      ))}
    </div>
  );
}

// ── Rating bars breakdown ─────────────────────────────────────
function RatingBars({ distribution, total }) {
  return (
    <div className="space-y-1.5">
      {[5, 4, 3, 2, 1].map(n => {
        const count = distribution[String(n)] || 0;
        const pct = total ? Math.round((count / total) * 100) : 0;
        return (
          <div key={n} className="flex items-center gap-2 text-xs">
            <span className="text-ink/55 w-4 text-right">{n}</span>
            <Star className="w-3 h-3 fill-honey text-honey-600" />
            <div className="flex-1 h-1.5 bg-ink/[0.07] rounded-full overflow-hidden">
              <div className="h-full bg-honey rounded-full transition-all" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-ink/45 w-7 text-right">{count}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Write Review Modal ────────────────────────────────────────
function ReviewModal({ productId, orderId, onClose, onSubmit }) {
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!rating) { setError('Please select a star rating.'); return; }
    setSubmitting(true); setError('');
    try {
      await axios.post(`${API}/products/${productId}/reviews`,
        { rating, title, body, order_id: orderId },
        { withCredentials: true }
      );
      onSubmit();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to submit review.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-white border border-[#1A232C] rounded-2xl w-full max-w-md"
        data-testid="review-modal"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-[#1A232C]">
          <h2 className="text-ink font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Write a Review</h2>
          <button onClick={onClose} className="text-ink/55 hover:text-ink"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-ink/55 text-xs uppercase tracking-widest block mb-2">Your Rating *</label>
            <Stars rating={rating} size="lg" interactive onRate={setRating} />
          </div>
          <div>
            <label className="text-ink/55 text-xs uppercase tracking-widest block mb-1.5">Review Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)} maxLength={100}
              placeholder="Summarise your experience..."
              data-testid="review-title-input"
              className="w-full bg-ink/[0.04] border border-ink/10 rounded-xl px-4 py-2.5 text-ink text-sm focus:outline-none focus:border-honey" />
          </div>
          <div>
            <label className="text-ink/55 text-xs uppercase tracking-widest block mb-1.5">Review</label>
            <textarea value={body} onChange={e => setBody(e.target.value)} rows={4} maxLength={1000}
              placeholder="Tell others what you think about this product..."
              data-testid="review-body-input"
              className="w-full bg-ink/[0.04] border border-ink/10 rounded-xl px-4 py-2.5 text-ink text-sm focus:outline-none focus:border-honey resize-none" />
          </div>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <div className="flex gap-3">
            <button onClick={handleSubmit} disabled={submitting || !rating}
              data-testid="submit-review-btn"
              className="flex-1 py-2.5 bg-honey text-black font-bold rounded-xl text-sm hover:bg-honey-600 transition-colors disabled:opacity-50">
              {submitting ? 'Submitting...' : 'Submit Review'}
            </button>
            <button onClick={onClose} className="px-5 py-2.5 bg-ink/[0.04] text-ink/75 rounded-xl text-sm">Cancel</button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ── Review Card ───────────────────────────────────────────────
function ReviewCard({ review, onHelpful }) {
  const initials = review.user_name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
  return (
    <div className="py-5 border-b border-ink/5" data-testid={`review-card-${review.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-honey/10 border border-honey/20 flex items-center justify-center text-honey-700 text-xs font-bold">
            {initials}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-ink text-sm font-semibold">{review.user_name}</span>
              {review.verified_purchase && (
                <span className="flex items-center gap-1 text-green-600 text-xs font-medium">
                  <ShieldCheck className="w-3 h-3" /> Verified
                </span>
              )}
            </div>
            <p className="text-ink/45 text-xs">{new Date(review.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</p>
          </div>
        </div>
        <Stars rating={review.rating} />
      </div>
      {review.title && <p className="text-ink font-semibold text-sm mb-1">{review.title}</p>}
      {review.body && <p className="text-ink/70 text-sm leading-relaxed">{review.body}</p>}
      <button onClick={() => onHelpful(review.id)}
        data-testid={`helpful-btn-${review.id}`}
        className="mt-3 flex items-center gap-1.5 text-ink/45 hover:text-ink/70 text-xs transition-colors">
        <ThumbsUp className="w-3 h-3" />
        Helpful {review.helpful_count > 0 && `(${review.helpful_count})`}
      </button>
    </div>
  );
}

// ── Reviews Section ───────────────────────────────────────────
function ReviewsSection({ productId, avgRating: initialAvgRating, reviewCount: initialReviewCount }) {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [reviews, setReviews] = useState([]);
  const [distribution, setDistribution] = useState({});
  const [total, setTotal] = useState(initialReviewCount || 0);
  const [avgRating, setAvgRating] = useState(initialAvgRating || 0);
  const [page, setPage] = useState(1);
  const [canReview, setCanReview] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => { fetchReviews(); }, [page]);

  useEffect(() => {
    if (user) checkEligibility();
    if (searchParams.get('review') === '1') setShowModal(true);
  }, [user]);

  const fetchReviews = async () => {
    try {
      const r = await axios.get(`${API}/products/${productId}/reviews`, { params: { page, limit: 5 } });
      setReviews(r.data.reviews);
      setTotal(r.data.total);
      setDistribution(r.data.distribution);
      // Recompute avg from distribution
      const dist = r.data.distribution;
      const tot = r.data.total;
      if (tot > 0) {
        const sum = [1,2,3,4,5].reduce((acc, n) => acc + n * (dist[String(n)] || 0), 0);
        setAvgRating(Math.round((sum / tot) * 10) / 10);
      }
    } catch {}
  };

  const checkEligibility = async () => {
    try {
      const r = await axios.get(`${API}/user/can-review/${productId}`, { withCredentials: true });
      setCanReview(r.data);
    } catch { setCanReview({ eligible: false }); }
  };

  const handleHelpful = async (reviewId) => {
    try {
      await axios.post(`${API}/reviews/${reviewId}/helpful`);
      setReviews(prev => prev.map(r => r.id === reviewId ? { ...r, helpful_count: (r.helpful_count || 0) + 1 } : r));
    } catch {}
  };

  const handleReviewSubmit = () => {
    setShowModal(false);
    setSuccess(true);
    setPage(1);
    fetchReviews();
    if (user) checkEligibility();
  };

  return (
    <div className="mt-12" data-testid="reviews-section">
      <AnimatePresence>
        {showModal && canReview?.eligible && (
          <ReviewModal
            productId={productId}
            orderId={canReview.order_id}
            onClose={() => setShowModal(false)}
            onSubmit={handleReviewSubmit}
          />
        )}
      </AnimatePresence>

      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-black text-ink" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Customer Reviews
        </h2>
        {user && canReview?.eligible && !success && (
          <button onClick={() => setShowModal(true)}
            data-testid="write-review-btn"
            className="flex items-center gap-2 px-4 py-2 bg-honey text-black text-sm font-bold rounded-xl hover:bg-honey-600 transition-colors">
            <Star className="w-4 h-4" /> Write a Review
          </button>
        )}
      </div>

      {success && (
        <div className="mb-4 p-4 bg-green-500/10 border border-green-500/20 rounded-xl text-green-600 text-sm flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 flex-shrink-0" /> Thank you for your review! It has been published.
        </div>
      )}

      {total > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* Summary */}
          <div className="glass-card rounded-2xl p-5 flex flex-col items-center justify-center gap-2 text-center">
            <span className="text-5xl font-black text-ink">{avgRating?.toFixed(1) || '0.0'}</span>
            <Stars rating={Math.round(avgRating || 0)} size="md" />
            <span className="text-ink/55 text-sm">{total} review{total !== 1 ? 's' : ''}</span>
          </div>
          {/* Distribution */}
          <div className="glass-card rounded-2xl p-5 md:col-span-2">
            <RatingBars distribution={distribution} total={total} />
          </div>
        </div>
      ) : (
        <div className="glass-card rounded-2xl p-8 text-center mb-6">
          <Star className="w-10 h-10 text-ink/15 mx-auto mb-3" />
          <p className="text-ink/55 text-sm">No reviews yet.</p>
          {user && canReview?.eligible && !success && (
            <button onClick={() => setShowModal(true)} data-testid="be-first-review-btn"
              className="mt-3 text-honey-600 text-sm font-bold hover:underline">
              Be the first to review
            </button>
          )}
          {!user && <p className="text-ink/45 text-xs mt-2">Log in and purchase to leave a review.</p>}
        </div>
      )}

      {/* Review list */}
      {reviews.length > 0 && (
        <div className="glass-card rounded-2xl px-5">
          {reviews.map(r => <ReviewCard key={r.id} review={r} onHelpful={handleHelpful} />)}
          {total > reviews.length && (
            <div className="py-4 text-center">
              <button onClick={() => setPage(p => p + 1)} className="text-honey-700 text-sm font-bold hover:underline">
                Load more reviews
              </button>
            </div>
          )}
        </div>
      )}

      {/* Not eligible message */}
      {user && canReview && !canReview.eligible && !success && total === 0 && (
        <p className="text-ink/35 text-xs text-center mt-2">
          {canReview.reason === 'You have already reviewed this product'
            ? 'You have already reviewed this product.'
            : 'Purchase and receive this item to leave a review.'}
        </p>
      )}
    </div>
  );
}


// ── Frequently bought together (buy-together deals) ──────────
function BoughtTogether({ productId }) {
  const { addToCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    axios.get(`${API}/products/${productId}/deals`).then(r => setDeals(r.data.deals)).catch(() => setDeals([]));
  }, [productId]);

  const addAll = async (deal) => {
    if (!user) { navigate('/auth'); return; }
    setBusy(deal.id);
    for (const item of deal.items) {
      if (!(await addToCart(item.id, 1))) break;
    }
    setBusy('');
    navigate('/cart');
  };

  if (!deals.length) return null;
  return (
    <div className="mt-16 space-y-6" data-testid="bought-together">
      <h2 className="text-2xl font-black text-ink" style={{ fontFamily: 'Outfit, sans-serif' }}>Frequently bought together</h2>
      {deals.map(deal => {
        const blocked = deal.items.some(needsChoices);
        return (
          <div key={deal.id} className="glass-card rounded-2xl p-6 flex flex-wrap items-center gap-6">
            <div className="flex flex-wrap items-center gap-3">
              {deal.items.map((item, i) => (
                <React.Fragment key={item.id}>
                  {i > 0 && <Plus className="w-4 h-4 text-ink/45" />}
                  <Link to={`/products/${item.id}`} className="w-20 text-center group">
                    <div className="w-20 h-20 rounded-xl overflow-hidden bg-ink/[0.04] mb-1">
                      {item.images?.[0] ? <img src={item.images[0]} alt={item.title} className="w-full h-full object-cover" /> : <Package className="w-8 h-8 text-ink/30 m-auto mt-6" />}
                    </div>
                    <p className="text-ink/70 text-xs truncate group-hover:text-ink">{item.title}</p>
                  </Link>
                </React.Fragment>
              ))}
            </div>
            <div className="flex-1 min-w-[200px]">
              <p className="text-ink font-bold">{deal.name}</p>
              <p className="flex items-baseline gap-2"><PriceTag now={deal.price} was={deal.value} className="text-[15px]" />
                <span className="text-green-600 text-sm">save ${(deal.value - deal.price).toFixed(2)}</span></p>
              <p className="text-ink/45 text-xs">Discount applies automatically in your cart.</p>
            </div>
            {blocked ? (
              <p className="text-ink/55 text-sm max-w-[200px]">Some of these have options. Add each one from its page to get the deal.</p>
            ) : (
              <button onClick={() => addAll(deal)} disabled={busy === deal.id} className="btn-primary flex items-center gap-2">
                <ShoppingCart className="w-4 h-4" /> {busy === deal.id ? 'Adding…' : `Add all ${deal.items.length} to cart`}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Main ProductDetail ────────────────────────────────────────
export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, loading } = useCart();
  const { user } = useAuth();
  const { toggleWishlist, isWishlisted } = useWishlist();
  const [product, setProduct] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [choices, setChoices] = useState({});
  const [personal, setPersonal] = useState({});
  const [choiceError, setChoiceError] = useState('');
  const saleCountdown = useCountdown(product?.sale?.ends_at);

  useEffect(() => {
    axios.get(`${API}/products/${id}`)
      .then(r => { setProduct(r.data); setChoices({}); setPersonal({}); addRecentlyViewed(r.data.id); })
      .catch(() => navigate('/'))
      .finally(() => setFetching(false));
  }, [id]);

  // Search engine title + meta description for this product
  useEffect(() => {
    if (!product) return;
    const prevTitle = document.title;
    document.title = product.seo_title || `${product.title} | Market @ Buzzzillion`;
    let meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute('content');
    if (!meta) { meta = document.createElement('meta'); meta.setAttribute('name', 'description'); document.head.appendChild(meta); }
    meta.setAttribute('content', product.seo_description || (product.description || '').slice(0, 155));
    return () => { document.title = prevTitle; if (prevDesc != null) meta.setAttribute('content', prevDesc); };
  }, [product]);

  const handleAddToCart = async () => {
    if (!user) { navigate('/auth'); return; }
    const missingOption = (product.options || []).find(o => !choices[o.name]);
    if (missingOption) { setChoiceError(`Please choose a ${missingOption.name.toLowerCase()}`); return; }
    const missingText = (product.personalizations || []).find(f => f.required && !(personal[f.label] || '').trim());
    if (missingText) { setChoiceError(`Please fill in "${missingText.label}"`); return; }
    setChoiceError('');
    if (!(await addToCart(id, quantity, { options: choices, personalization: personal }))) return;
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  if (fetching) return (
    <div className="store-page flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-honey border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!product) return null;

  const { now: basePrice, was: wasPrice, pct: discount } = displayPrice(product);
  const tracked = product.track_inventory !== false;
  const addOn = (product.options || []).reduce((sum, o) => sum + (o.values.find(v => v.label === choices[o.name])?.price_delta || 0), 0)
    + (product.personalizations || []).reduce((sum, f) => sum + ((personal[f.label] || '').trim() ? (f.price_delta || 0) : 0), 0);
  const outOfStock = tracked && product.inventory <= 0;
  const maxQty = tracked ? Math.max(1, product.inventory) : 99;

  return (
    <div className="store-page relative">
      <Navbar />
      <div className="relative z-10 max-w-6xl mx-auto px-6 pt-8 pb-20">
        <button onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-ink/55 hover:text-ink transition-colors mb-8 text-sm">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Images */}
          <div>
            <motion.div
              className="glass-card rounded-2xl overflow-hidden aspect-square mb-4"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              {product.images?.[selectedImage] ? (
                <img src={product.images[selectedImage]} alt={product.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Package className="w-16 h-16 text-ink/30" />
                </div>
              )}
            </motion.div>
            {product.images?.length > 1 && (
              <div className="flex gap-3">
                {product.images.slice(0, 4).map((img, i) => (
                  <button key={i} onClick={() => setSelectedImage(i)}
                    className={`w-16 h-16 rounded-xl overflow-hidden border-2 transition-all ${selectedImage === i ? 'border-honey' : 'border-ink/10'}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            {product.category && (
              <p className="text-xs uppercase tracking-[0.3em] text-honey-700 font-bold mb-3">{product.category}</p>
            )}
            <h1 className="text-3xl font-black text-ink mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {product.title}
            </h1>

            {/* Avg rating summary */}
            {product.review_count > 0 && (
              <div className="flex items-center gap-2 mb-4">
                <Stars rating={Math.round(product.avg_rating || 0)} size="sm" />
                <span className="text-ink/55 text-sm">{product.avg_rating?.toFixed(1)} ({product.review_count} review{product.review_count !== 1 ? 's' : ''})</span>
              </div>
            )}

            <div className="flex items-center gap-4 mb-6">
              <PriceTag now={basePrice + addOn} was={wasPrice ? wasPrice + addOn : null} className="text-[25px]" />
              {wasPrice && <span className="bg-coral text-white text-sm font-black px-3 py-1 rounded-full">{discount}% OFF</span>}
            </div>
            {product.sale && (
              <p className="flex items-center gap-2 text-coral-600 text-sm font-bold -mt-3 mb-5" data-testid="sale-banner">
                <Clock className="w-4 h-4" /> {product.sale.name}{saleCountdown ? ` · ends in ${saleCountdown}` : ''}
              </p>
            )}
            <PromoSlot placement="product" productId={id} className="mb-6" />

            {product.bundle && (
              <div className="mb-6 p-4 rounded-2xl bg-ink/[0.04] border border-ink/10">
                <p className="text-ink/60 text-xs uppercase tracking-widest mb-3">What's included</p>
                <div className="space-y-2">
                  {product.bundle.items.map(item => (
                    <Link key={item.id} to={`/products/${item.id}`} className="flex items-center gap-3 group">
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-ink/[0.04] flex-shrink-0">
                        {item.image && <img src={item.image} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <span className="text-ink/85 text-sm flex-1 group-hover:text-ink">{item.quantity > 1 ? `${item.quantity} × ` : ''}{item.title}</span>
                      <span className="text-ink/55 text-sm">${(item.price * item.quantity).toFixed(2)}</span>
                    </Link>
                  ))}
                </div>
                {product.bundle.savings > 0 && (
                  <p className="text-green-600 text-sm font-bold mt-3">You save ${product.bundle.savings.toFixed(2)} vs. buying separately (${product.bundle.value.toFixed(2)})</p>
                )}
              </div>
            )}

            {product.description && (
              <p className="text-ink/70 text-base leading-relaxed mb-6 whitespace-pre-line">{product.description}</p>
            )}

            {(product.options || []).map(opt => (
              <div key={opt.name} className="mb-5" data-testid={`option-${opt.name}`}>
                <p className="text-ink/60 text-xs uppercase tracking-widest mb-2">
                  {opt.name}{choices[opt.name] ? <span className="text-ink normal-case tracking-normal ml-2">{choices[opt.name]}</span> : null}
                </p>
                <div className="flex flex-wrap gap-2">
                  {opt.values.map(v => (
                    <button key={v.label} type="button" onClick={() => { setChoices(c => ({ ...c, [opt.name]: v.label })); setChoiceError(''); }}
                      className={`px-4 py-2 rounded-xl border text-sm font-medium transition-all ${choices[opt.name] === v.label ? 'bg-honey/10 border-honey text-honey-700' : 'bg-ink/[0.04] border-ink/10 text-ink/75 hover:border-ink/20'}`}>
                      {v.label}{v.price_delta ? <span className="text-xs opacity-70 ml-1">{v.price_delta > 0 ? '+' : '−'}${Math.abs(v.price_delta).toFixed(2)}</span> : null}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {(product.personalizations || []).length > 0 && (
              <div className="mb-6 space-y-3">
                {product.personalizations.map(f => {
                  const val = personal[f.label] || '';
                  return (
                    <div key={f.label}>
                      <label className="text-ink/60 text-xs uppercase tracking-widest mb-1 flex justify-between">
                        <span>{f.label}{f.required ? ' *' : ' (optional)'}{f.price_delta ? <span className="normal-case tracking-normal text-ink/55 ml-1">+${f.price_delta.toFixed(2)}</span> : null}</span>
                        <span className="normal-case tracking-normal">{val.length}/{f.max_length}</span>
                      </label>
                      <input value={val} maxLength={f.max_length} placeholder={f.placeholder || ''}
                        onChange={e => { setPersonal(p => ({ ...p, [f.label]: e.target.value })); setChoiceError(''); }}
                        className="w-full bg-ink/[0.04] border border-ink/10 rounded-xl px-4 py-3 text-ink placeholder-ink/40 focus:outline-none focus:border-honey" />
                    </div>
                  );
                })}
              </div>
            )}

            {product.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {product.tags.map(tag => (
                  <span key={tag} className="px-3 py-1 bg-ink/[0.04] border border-ink/10 rounded-full text-ink/60 text-xs flex items-center gap-1">
                    <Tag className="w-3 h-3" /> {tag}
                  </span>
                ))}
              </div>
            )}

            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center gap-2 bg-ink/[0.04] border border-ink/10 rounded-xl overflow-hidden">
                <button onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  className="px-4 py-3 text-ink hover:bg-ink/[0.07] transition-colors font-bold">-</button>
                <span className="px-4 py-3 text-ink font-bold min-w-[3rem] text-center">{quantity}</span>
                <button onClick={() => setQuantity(q => Math.min(maxQty, q + 1))}
                  className="px-4 py-3 text-ink hover:bg-ink/[0.07] transition-colors font-bold">+</button>
              </div>
              <span className="text-ink/45 text-sm">
                {outOfStock ? <span className="text-red-600 font-bold">Out of stock</span> : tracked && product.inventory <= 5 ? `Only ${product.inventory} left` : 'In stock'}
              </span>
            </div>

            {choiceError && <p className="text-red-600 text-sm mb-3">{choiceError}</p>}
            <div className="flex gap-3">
            <button onClick={handleAddToCart} disabled={loading || outOfStock}
              data-testid="add-to-cart-detail-btn"
              className={`flex-1 py-4 rounded-2xl font-bold text-lg flex items-center justify-center gap-3 transition-all disabled:opacity-40 disabled:cursor-not-allowed ${added ? 'bg-green-500 text-ink' : 'btn-primary'}`}>
              <ShoppingCart className="w-5 h-5" />
              {outOfStock ? 'Out of Stock' : added ? 'Added to Cart!' : 'Add to Cart'}
            </button>
            <button onClick={() => toggleWishlist(id)}
              data-testid="wishlist-detail-btn"
              className={`p-4 rounded-2xl border transition-all ${isWishlisted(id) ? 'bg-coral border-coral text-white' : 'bg-white border-ink/15 text-ink/60 hover:text-coral-600 hover:border-coral/40'}`}>
              <Heart className={`w-6 h-6 ${isWishlisted(id) ? 'fill-white' : ''}`} />
            </button>
            </div>
          </motion.div>
        </div>

        <BoughtTogether productId={id} />

        {/* Reviews Section */}
        <ReviewsSection
          productId={id}
          avgRating={product.avg_rating}
          reviewCount={product.review_count}
        />
      </div>
    </div>
  );
}
